<template>
  <div class="create-poll-form">
    <div class="create-poll-form__header">
      <v-icon
        x-small
        color="primary"
        class="mr-1"
      >
        mdi-plus-circle
      </v-icon>
      <span>Create Poll</span>
    </div>
    <div class="create-poll-form__body">
      <v-text-field
        v-model="question"
        label="Question"
        outlined
        dense
        hide-details="auto"
        class="mb-2"
        :rules="[v => !!v || 'Required']"
      />

      <v-radio-group
        v-model="pollType"
        row
        class="mt-0 mb-1 create-poll-form__type-group"
        hide-details
      >
        <v-radio
          label="Single choice"
          value="single"
        />
        <v-radio
          label="Multiple choice"
          value="multi"
        />
      </v-radio-group>

      <v-checkbox
        v-model="allowOther"
        label="Include 'Other' option"
        hide-details
        dense
        class="mt-0 mb-2 create-poll-form__checkbox"
      />

      <div class="create-poll-form__options">
        <div
          v-for="(option, idx) in options"
          :key="idx"
          class="create-poll-form__option-row"
        >
          <v-text-field
            v-model="options[idx]"
            :label="`Option ${idx + 1}`"
            dense
            outlined
            hide-details
          />
          <button
            class="create-poll-form__remove-btn"
            :disabled="options.length <= 2"
            @click="removeOption(idx)"
          >
            <v-icon small>
              mdi-close
            </v-icon>
          </button>
        </div>
      </div>

      <button
        class="create-poll-form__add-btn"
        :disabled="options.length >= 7"
        @click="addOption"
      >
        <v-icon
          x-small
          class="mr-1"
        >
          mdi-plus
        </v-icon>
        Add Option
      </button>

      <div
        v-if="errorMessage"
        class="create-poll-form__error"
      >
        {{ errorMessage }}
      </div>
    </div>
    <div class="create-poll-form__actions">
      <button
        class="create-poll-form__cancel-btn"
        @click="$emit('cancel')"
      >
        Cancel
      </button>
      <button
        class="create-poll-form__launch-btn"
        :disabled="!isValid || loading"
        @click="submit"
      >
        <v-icon
          x-small
          class="mr-1"
        >
          mdi-rocket-launch
        </v-icon>
        {{ loading ? 'Creating...' : 'Launch Poll' }}
      </button>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'CreatePollForm',

  emits: ['cancel', 'created'],

  data() {
    return {
      question: '',
      pollType: 'single',
      allowOther: false,
      options: ['', ''],
      loading: false,
      errorMessage: '',
    };
  },

  computed: {
    isValid() {
      const filledOptions = this.options.filter((o) => o.trim().length > 0);
      return this.question.trim().length > 0 && filledOptions.length >= 2;
    },
  },

  methods: {
    ...mapActions('webinarChat', ['createPoll']),

    addOption() {
      if (this.options.length < 7) {
        this.options.push('');
      }
    },

    removeOption(idx) {
      if (this.options.length > 2) {
        this.options.splice(idx, 1);
      }
    },

    async submit() {
      if (!this.isValid || this.loading) return;
      this.errorMessage = '';
      this.loading = true;
      try {
        const filledOptions = this.options.filter((o) => o.trim().length > 0);
        await this.createPoll({
          question: this.question.trim(),
          pollType: this.pollType,
          allowOther: this.allowOther,
          options: filledOptions,
        });
        this.$emit('created');
        this.reset();
      } catch {
        this.errorMessage = 'Failed to create poll. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    reset() {
      this.question = '';
      this.pollType = 'single';
      this.allowOther = false;
      this.options = ['', ''];
    },
  },
};
</script>

<style lang="scss" scoped>
@use "sass:color";
.create-poll-form {
  margin-bottom: 8px;
  border-radius: 10px;
  border: 1.5px solid rgba(244, 119, 33, 0.35);
  background: #fff;
  overflow: hidden;

  &__header {
    display: flex;
    align-items: center;
    padding: 10px 12px 6px;
    font-size: 13px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.8);
  }

  &__body {
    padding: 0 12px 6px;

    ::v-deep .v-text-field .v-input__slot {
      min-height: 28px !important;
    }

    ::v-deep .v-text-field input {
      padding: 4px 8px;
      font-size: 12px;
    }

    ::v-deep .v-text-field .v-label {
      font-size: 12px;
      top: 5px;
    }

    ::v-deep .v-text-field .v-label--active {
      transform: translateY(-14px) scale(0.75);
    }
  }

  &__type-group {
    ::v-deep .v-label {
      font-size: 12px;
    }

    ::v-deep .v-icon {
      font-size: 18px !important;
    }

    ::v-deep .v-input--selection-controls__input {
      margin-right: 4px;
    }
  }

  &__checkbox {
    ::v-deep .v-label {
      font-size: 12px;
    }

    ::v-deep .v-icon {
      font-size: 18px !important;
    }

    ::v-deep .v-input--selection-controls__input {
      margin-right: 4px;
    }
  }

  &__options {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 6px;
  }

  &__option-row {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__remove-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    min-width: 24px;
    border: none;
    border-radius: 4px;
    background: transparent;
    color: rgba(0, 0, 0, 0.4);
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;

    &:hover:not(:disabled) {
      background: rgba(0, 0, 0, 0.06);
      color: rgba(0, 0, 0, 0.7);
    }

    &:disabled {
      opacity: 0.3;
      cursor: default;
    }
  }

  &__add-btn {
    display: inline-flex;
    align-items: center;
    padding: 0;
    border: none;
    background: transparent;
    color: #f47721;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.15s ease;

    &:hover:not(:disabled) {
      opacity: 0.75;
    }

    &:disabled {
      opacity: 0.35;
      cursor: default;
    }
  }

  &__error {
    color: #b00020;
    font-size: 11px;
    margin-top: 6px;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
    padding: 6px 12px 10px;
  }

  &__cancel-btn {
    padding: 5px 12px;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: rgba(0, 0, 0, 0.5);
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
      background: rgba(0, 0, 0, 0.05);
    }
  }

  &__launch-btn {
    display: inline-flex;
    align-items: center;
    padding: 5px 14px;
    border: none;
    border-radius: 6px;
    background: #f47721;
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease, opacity 0.15s ease;

    &:hover:not(:disabled) {
      background: color.adjust(#f47721, $lightness: -6%);
    }

    &:disabled {
      opacity: 0.45;
      cursor: default;
    }
  }
}
</style>
